/**
 * LDS split-payment slot lock (FE display only).
 * SSOT: orderReqData.payments[i] — no resolveLdsFinancialState (avoids circular import).
 */
import { LDS_FINANCIAL_MONEY_TOLERANCE } from "./resolveLdsFinancialState.js";
import { resolveLdsPaymentSlotCapturedStrict } from "./ldsSplitPaymentsSsot.js";

function truthyPaid(raw) {
  return raw === true || raw === 1 || raw === "true" || raw === "1";
}

function truthyUseJ5(raw) {
  return raw === true || raw === 1 || raw === "true" || raw === "1";
}

function toPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function orderPayments(order) {
  if (!Array.isArray(order?.payments)) return [];
  return order.payments.filter((p) => p && typeof p === "object");
}

function orderHasPartialCheckout(order) {
  const sumpaid = toPositiveNumber(order?.sumpaid);
  if (sumpaid > 0) return true;
  const status = String(order?.post_payment_checkout_status || "").trim();
  return status === "payment_authorized_partial";
}

function resolveOrderPaidPool(order) {
  const sumpaid = toPositiveNumber(order?.sumpaid);
  if (sumpaid > 0) return sumpaid;
  const adminPaid = toPositiveNumber(order?.admin_paid_amount ?? order?.adminPaidAmount);
  if (adminPaid > 0) return adminPaid;
  let fromDirect = 0;
  for (const p of orderPayments(order)) {
    fromDirect += directSlotContribution(p);
  }
  return fromDirect;
}

function directSlotContribution(payment) {
  if (!payment || typeof payment !== "object") return 0;
  if (truthyPaid(payment.paid)) {
    return toPositiveNumber(payment.actualAmount ?? payment.amount);
  }
  if (payment.j5_capture_state === "partial_captured") {
    return toPositiveNumber(
      payment.cumulative_captured_ils ?? payment.captured_amount_ils
    );
  }
  return Math.max(
    toPositiveNumber(payment.cumulative_captured_ils),
    toPositiveNumber(payment.captured_amount_ils)
  );
}

/**
 * Infer per-slot captured ILS when j5 fields are absent but order is partially paid.
 * Single payment slot only — multi-slot uses sequential allocation in resolveSlotCapturedIls.
 */
function inferSingleSlotContribution(payment, order) {
  const payments = orderPayments(order);
  if (payments.length !== 1) return 0;

  const tol = LDS_FINANCIAL_MONEY_TOLERANCE;
  let remainingPaid = resolveOrderPaidPool(order);
  const partialCheckout = orderHasPartialCheckout(order);

  let contrib = directSlotContribution(payment);
  if (contrib <= tol && partialCheckout && remainingPaid > tol && payment?.url) {
    const slotAmt = toPositiveNumber(payment.actualAmount ?? payment.amount);
    if (slotAmt > 0 && slotAmt <= remainingPaid + tol) {
      contrib = slotAmt;
    }
  }
  return contrib > tol ? contrib : 0;
}

/** Multi-slot: direct fields on payments[i] only — never redistribute sumpaid pool. */
function resolveMultiSlotDirectCapture(payment) {
  const direct = directSlotContribution(payment);
  if (direct > 0) return direct;
  if (truthyPaid(payment?.paid)) {
    return toPositiveNumber(
      payment.actualAmount ?? payment.actual_amount ?? payment.amount
    );
  }
  return 0;
}

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/**
 * @param {object|null|undefined} payment
 * @param {object|null|undefined} [order]
 * @param {number} [cardIndex]
 */
export function isLdsSplitPaymentSlotLocked(payment, order = null, cardIndex = 0) {
  if (payment && typeof payment === "object") {
    if (truthyPaid(payment.paid)) return true;
    if (payment.j5_capture_state === "partial_captured") return true;
    if (toPositiveNumber(payment.cumulative_captured_ils) > 0) return true;
    if (toPositiveNumber(payment.captured_amount_ils) > 0) return true;
  }

  if (!order || typeof order !== "object") return false;

  const payments = orderPayments(order);
  const idx = Number(cardIndex);

  if (payments.length > 1 && Number.isFinite(idx) && idx >= 0 && idx < payments.length) {
    return (
      getLdsSplitPaymentSlotCapturedIls(payments[idx], order, idx) >
      LDS_FINANCIAL_MONEY_TOLERANCE
    );
  }

  if (payments.length === 1) {
    const contrib = inferSingleSlotContribution(payments[0], order);
    if (idx === 0 && contrib > LDS_FINANCIAL_MONEY_TOLERANCE) return true;
  }

  if (
    idx === 0 &&
    orderPayments(order).length === 0 &&
    orderHasPartialCheckout(order)
  ) {
    return resolveOrderPaidPool(order) > LDS_FINANCIAL_MONEY_TOLERANCE;
  }

  return false;
}

/** @param {object|null|undefined} payment */
export function isLdsSplitPaymentSlotJ5(payment, order = null) {
  if (payment && typeof payment === "object") {
    if (truthyUseJ5(payment.useJ5) || truthyUseJ5(payment.use_j5)) return true;
  }
  if (order && typeof order === "object") {
    const status = String(order.post_payment_checkout_status || "").trim();
    if (status === "payment_authorized_partial") return true;
    if (orderPayments(order).some((p) => truthyUseJ5(p?.useJ5) || truthyUseJ5(p?.use_j5))) {
      return true;
    }
  }
  return false;
}

/**
 * @param {object|null|undefined} payment
 * @param {object|null|undefined} [order]
 * @param {number} [cardIndex]
 * @returns {number}
 */
export function getLdsSplitPaymentSlotCapturedIls(payment, order = null, cardIndex = 0) {
  const direct = directSlotContribution(payment);
  if (direct > 0) return direct;

  if (payment && typeof payment === "object" && truthyPaid(payment.paid)) {
    return toPositiveNumber(payment.actualAmount ?? payment.amount);
  }

  if (order && typeof order === "object") {
    const payments = orderPayments(order);
    const idx = Number(cardIndex);

    if (payments.length > 1 && Number.isFinite(idx) && idx >= 0 && idx < payments.length) {
      const strict = resolveLdsPaymentSlotCapturedStrict(payment, order, idx);
      if (strict > 0) return strict;
      return resolveMultiSlotDirectCapture(payment);
    }

    if (payments.length === 1 && idx === 0) {
      const single = inferSingleSlotContribution(payments[0], order);
      if (single > 0) return single;
    }

    if (
      idx === 0 &&
      orderPayments(order).length === 0 &&
      orderHasPartialCheckout(order)
    ) {
      return resolveOrderPaidPool(order);
    }
  }

  return 0;
}

/**
 * @param {object|null|undefined} payment
 * @param {object|null|undefined} [order]
 * @param {number} [cardIndex]
 */
export function resolveLdsSplitPaymentSlotLockUi(payment, order = null, cardIndex = 0) {
  const locked = isLdsSplitPaymentSlotLocked(payment, order, cardIndex);
  if (!locked) {
    return { locked: false, badgeLabel: null, capturedIls: 0, isJ5: false };
  }
  const isJ5 = isLdsSplitPaymentSlotJ5(payment, order);
  const isJ5Partial =
    payment?.j5_capture_state === "partial_captured" ||
    (isJ5 && !truthyPaid(payment?.paid));
  return {
    locked: true,
    badgeLabel: isJ5Partial
      ? "התשלום נתפס בהצלחה"
      : "התשלום התקבל בהצלחה",
    capturedIls: getLdsSplitPaymentSlotCapturedIls(payment, order, cardIndex),
    isJ5,
  };
}
